import { useMsal } from '@azure/msal-react';
import { useEffect, useRef, useState } from 'react';
import { BrowserAuthError, InteractionStatus } from '@azure/msal-browser';
import { BluLoader } from '@bludata/components';
import EmptyPage from '../utility_objects/EmptyPage';
import localforage from 'localforage';
import { logLoginFlow, logAccountState, logConsumerRef } from './loginLogger';
import { clearMsalCookies } from './loginMSALAuthProcess';
import { tryAcquireInteractionLock } from './AuthInteractionLock';
import { SESSION_TIMEOUT_KEY } from './useLoginMsal';
import { logAuthTrace } from './loginLogger';

/**
 * Rimuove tutte le chiavi MSAL di interazione/temporanee da localStorage e sessionStorage.
 * NON rimuove le chiavi di cache account/token (che contengono "-login.windows.net-")
 * a meno che non si voglia forzare un re-login completo.
 */
function clearMsalInteractionState(clearAll = false) {
  // Chiavi temporanee/interazione MSAL da rimuovere sempre
  const interactionPatterns = [
    'msal.interaction.status',
    'msal.browser.interaction.status',
    'msal.temp',
  ];

  [localStorage, sessionStorage].forEach((storage) => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key) continue;
      const lk = key.toLowerCase();

      if (clearAll && lk.startsWith('msal')) {
        keysToRemove.push(key);
      } else if (
        interactionPatterns.some((p) => lk.includes(p)) ||
        (lk.startsWith('msal.') && lk.includes('request'))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => storage.removeItem(k));
  });
}

/**
 * LoginPage — Responsabile ESCLUSIVAMENTE di avviare loginRedirect() verso B2C.
 *
 * Viene renderizzata solo quando:
 * - useIsAuthenticated() === false (nessun account MSAL in cache)
 * - isLoadingToken === false (nessun caricamento in corso)
 * - inProgress === InteractionStatus.None (MSAL non sta processando redirect/login)
 *
 * NON gestisce:
 * - handleRedirectPromise → gestito da MsalProvider internamente
 * - acquireTokenSilent → gestito da useLoginMsal.acquireAndScheduleToken
 * - initialize() → gestito da LoginMSALProvider
 */
function LoginPage() {
  const { instance: msalInstance, inProgress } = useMsal();
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);
  const hasTriedCleanup = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    const performLogin = async () => {
      if (!isMountedRef.current) return;
      logLoginFlow('LOGIN_PAGE_MOUNTED');

      if (!msalInstance) {
        console.error('[LoginPage] MSAL instance non disponibile');
        if (isMountedRef.current) setLoading(false);
        return;
      }

      // Guard: non chiamare loginRedirect se MSAL ha un'interazione in corso
      // (es. logoutRedirect non ancora completato dopo wake-from-sleep)
      if (inProgress !== InteractionStatus.None) {
        console.warn(
          '[LoginPage] Interazione MSAL in corso, attendo completamento:',
          inProgress,
        );
        return;
      }

      const savedLocalStorage = await localforage.getItem<string>(
        'terminal_FOCUSCLOUD_workplaceRef',
      );
      const savedAgencyName = await localforage.getItem<string>(
        'terminal_FOCUSCLOUD_workplaceName',
      );

      logConsumerRef(
        savedLocalStorage,
        savedLocalStorage ? 'retrieved' : 'missing',
      );

      const account = msalInstance.getActiveAccount();
      logAccountState(account, 'getActiveAccount');

      if (!account) {
        // Guard: se l'URL contiene già un auth code B2C, il redirect è in corso
        if (/[?&#]code=/.test(window.location.href)) {
          console.warn(
            '[LoginPage] URL contiene auth code, skip loginRedirect',
          );
          return;
        }

        // Mutex: evita redirect concorrenti con altri timer/hook
        if (
          !tryAcquireInteractionLock('loginPage', 'loginRedirect da LoginPage')
        ) {
          console.warn(
            '[LoginPage] Interazione MSAL già in corso, skip loginRedirect',
          );
          return;
        }

        // Nessun account in cache → redirect a B2C per autenticazione
        logLoginFlow('NO_ACCOUNT_REDIRECTING');
        // Previene che un timestamp stale della sessione precedente causi un logout
        // spurio subito dopo il ritorno da B2C (pattern: msalChangePsw fa lo stesso)
        localStorage.removeItem(SESSION_TIMEOUT_KEY);
        logAuthTrace('LoginPage:performLogin', {
          inProgress,
          hasAccount: false,
        });
        try {
          await msalInstance.loginRedirect({
            scopes: ['openid'],
            extraQueryParameters: {
              consumerRef: savedLocalStorage || undefined,
              customerName: savedAgencyName || undefined,
            },
          });
        } catch (error) {
          if (
            error instanceof BrowserAuthError &&
            error.errorCode === 'interaction_in_progress'
          ) {
            // MSAL ha un lock di interazione orfano. Il lock è persistito nei cookie
            // (storeAuthStateInCookie: true) e/o localStorage, quindi handleRedirectPromise
            // e window.location.reload() da soli non lo risolvono.
            // Strategia: pulizia completa dello stato MSAL + cookie + reload una volta sola.
            if (!hasTriedCleanup.current) {
              hasTriedCleanup.current = true;
              console.warn(
                '[LoginPage] interaction_in_progress — pulizia stato MSAL e reload',
              );
              logAuthTrace('LoginPage:interaction_in_progress', {
                hasTriedCleanup: false,
              });

              // 1. Pulisci cookie MSAL (contengono il lock di interazione)
              clearMsalCookies();
              // 2. Pulisci chiavi di interazione da localStorage/sessionStorage
              clearMsalInteractionState(false);
              // 3. Prova a processare redirect pendenti per rilasciare il lock in-memory
              try {
                await msalInstance.handleRedirectPromise();
              } catch {
                // Ignora — il lock potrebbe non essere rilasciabile in-memory
              }

              // 4. Reload: con cookie e storage puliti, MSAL partirà senza lock
              window.location.reload();
            } else {
              // Siamo già passati dal cleanup + reload e il lock persiste ancora.
              // Pulizia aggressiva: rimuovi TUTTE le chiavi MSAL e forza reload.
              console.error(
                '[LoginPage] Lock MSAL persistente dopo cleanup, pulizia totale',
              );
              clearMsalCookies();
              clearMsalInteractionState(true); // rimuovi tutto, inclusi account/token
              window.location.reload();
            }
          } else {
            console.error('[LoginPage] Errore durante loginRedirect:', error);
            if (isMountedRef.current) setLoading(false);
          }
        }
        return;
      }

      // Se c'è un account ma siamo finiti qui, vuol dire che useIsAuthenticated()
      // ha già restituito false e il token è gestito da useLoginMsal.
      // Non facciamo nulla: il Connector ci smonterà quando lo stato cambierà.
      logLoginFlow('ACCOUNT_EXISTS_WAITING', {
        accountId: account.homeAccountId,
      });
      if (isMountedRef.current) setLoading(false);
    };

    performLogin();

    return () => {
      isMountedRef.current = false;
    };
  }, [msalInstance, inProgress]);

  return <EmptyPage>{loading && <BluLoader open />}</EmptyPage>;
}

export default LoginPage;
